import { useEffect, useState } from "react";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { Modal } from "../ui/Modal";
import { Select } from "../ui/Select";
import api from "../../lib/api";

export interface Contact {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  address?: string;
  zipCode?: string;
  city?: string;
  country?: string;
  kvkNumber?: string;
  vatNumber?: string;
  iban?: string;
  type?: string;
}

export const CONTACT_TYPE_OPTIONS = [
  { value: "CUSTOMER", label: "Klant" },
  { value: "SUPPLIER", label: "Leverancier" },
  { value: "COMPANY", label: "Bedrijf" },
];

export function contactTypeLabel(t?: string) {
  return CONTACT_TYPE_OPTIONS.find((o) => o.value === t)?.label || "Klant";
}

interface ContactFormModalProps {
  isOpen: boolean;
  contact?: Contact | null; // voor bewerken
  defaultType?: string; // standaardtype bij nieuw (bijv. CUSTOMER)
  onClose: () => void;
  onSaved: (contact: Contact) => void;
}

export function ContactFormModal({
  isOpen,
  contact,
  defaultType = "CUSTOMER",
  onClose,
  onSaved,
}: ContactFormModalProps) {
  const isEdit = !!contact;
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [zip, setZip] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("Nederland");
  const [kvk, setKvk] = useState("");
  const [vat, setVat] = useState("");
  const [iban, setIban] = useState("");
  const [type, setType] = useState("CUSTOMER");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // (Her)initialiseer velden als de modal opent of het contact verandert.
  useEffect(() => {
    if (!isOpen) return;
    if (contact) {
      setName(contact.name || "");
      setEmail(contact.email || "");
      setPhone(contact.phone || "");
      setAddress(contact.address || "");
      setZip(contact.zipCode || "");
      setCity(contact.city || "");
      setCountry(contact.country || "Nederland");
      setKvk(contact.kvkNumber || "");
      setVat(contact.vatNumber || "");
      setIban(contact.iban || "");
      setType(contact.type || "CUSTOMER");
    } else {
      setName("");
      setEmail("");
      setPhone("");
      setAddress("");
      setZip("");
      setCity("");
      setCountry("Nederland");
      setKvk("");
      setVat("");
      setIban("");
      setType(defaultType);
    }
    setError("");
  }, [isOpen, contact, defaultType]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!name.trim()) {
      setError("Naam is verplicht");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        name,
        email,
        phone,
        address,
        zipCode: zip,
        city,
        country,
        kvkNumber: kvk,
        vatNumber: vat,
        iban,
        type,
      };
      if (isEdit) {
        const { data } = await api.patch(`/api/contacts/${contact!.id}`, payload);
        onSaved({ ...contact!, ...data, name: data.name || name });
      } else {
        const { data } = await api.post("/api/contacts", payload);
        onSaved({ ...data, name: data.name || name });
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || "Opslaan mislukt");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? "Contact bewerken" : "Nieuw contact"}
      subtitle={isEdit ? "Pas de gegevens aan" : "Voeg een klant, leverancier of bedrijf toe"}
      size="md"
    >
      <form onSubmit={submit} className="space-y-4">
        <Select
          label="Type"
          options={CONTACT_TYPE_OPTIONS}
          value={type}
          onChange={(e) => setType(e.target.value)}
        />
        <Input label="Naam *" value={name} onChange={(e) => setName(e.target.value)} required />
        <div className="grid grid-cols-2 gap-4">
          <Input label="E-mail" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <Input label="Telefoon" value={phone} onChange={(e) => setPhone(e.target.value)} />
        </div>
        <Input label="Adres" value={address} onChange={(e) => setAddress(e.target.value)} />
        <div className="grid grid-cols-2 gap-4">
          <Input label="Postcode" value={zip} onChange={(e) => setZip(e.target.value)} />
          <Input label="Plaats" value={city} onChange={(e) => setCity(e.target.value)} />
        </div>
        <Input label="Land" value={country} onChange={(e) => setCountry(e.target.value)} />
        <div className="grid grid-cols-2 gap-4">
          <Input label="KVK-nummer" value={kvk} onChange={(e) => setKvk(e.target.value)} />
          <Input label="BTW-nummer" value={vat} onChange={(e) => setVat(e.target.value)} />
        </div>
        <Input label="IBAN" value={iban} onChange={(e) => setIban(e.target.value)} />

        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={loading}>
            Annuleren
          </Button>
          <Button type="submit" loading={loading}>
            {isEdit ? "Wijzigingen opslaan" : "Contact opslaan"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}